import { DateRange } from "react-day-picker";

import { useQuery } from "@tanstack/react-query";

import { supabase } from "~/integrations/supabase/client";

interface LatePayment {
  id: string;
  total_amount?: number;
  balance?: number;
  payment_due_date: string;
  client: { name: string; tax_id: string };
  daysLate: number;
  paid_amount: number;
  remaining_amount: number;
  payments?: any[];
  source: "sales_request" | "manual_facturas";
  doc_number?: string;
  doc_type?: string;
  proportional_interest?: number;
  interest_left?: number;
  interest_paid?: number;
  principal_paid?: number;
  progress_percentage?: number;
}

export const useLatePayments = (dateRange?: DateRange) => {
  const { data: interestConfig, isLoading: isLoadingInterest } = useQuery({
    queryKey: ["monthly-late-interest-rate"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payment_config")
        .select("*")
        .eq("key", "monthly_late_interest_rate")
        .single();
      if (error && error.code !== "PGRST116") {
        console.error("Error fetching interest config:", error);
      }
      return data ? parseFloat(data.value) : 0;
    },
  });

  const { data: latePayments, isLoading: isLoadingLatePayments } = useQuery({
    queryKey: [
      "late-payments",
      dateRange?.from?.toISOString(),
      dateRange?.to?.toISOString(),
      interestConfig,
    ],
    queryFn: async () => {
      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Get late sales requests
        let salesRequestQuery = supabase
          .from("sales_request")
          .select("*, client:clients(name, tax_id)")
          .not("status", "in", "(test,rejected)")
          .eq("paid", false)
          .lt("payment_due_date", today.toISOString());

        // Apply date range filter if provided (filter by payment_due_date)
        if (dateRange?.from) {
          const fromDate = new Date(dateRange.from);
          fromDate.setHours(0, 0, 0, 0);
          salesRequestQuery = salesRequestQuery.gte(
            "payment_due_date",
            fromDate.toISOString()
          );

          if (dateRange.to) {
            const toDate = new Date(dateRange.to);
            toDate.setHours(23, 59, 59, 999);
            salesRequestQuery = salesRequestQuery.lte(
              "payment_due_date",
              toDate.toISOString()
            );
          }
        }

        const { data: salesRequestData, error: salesRequestError } =
          await salesRequestQuery;

        if (salesRequestError) {
          console.error(
            "Error fetching late sales requests:",
            salesRequestError
          );
        }

        // Get late manual facturas
        let manualFacturasQuery = supabase
          .from("manual_facturas")
          .select(
            `
            *,
            clients!inner (
              name,
              tax_id
            )
          `
          )
          .gt("balance", 0)
          .lt("payment_due_date", today.toISOString());

        // Apply date range filter if provided (filter by payment_due_date)
        if (dateRange?.from) {
          const fromDate = new Date(dateRange.from);
          fromDate.setHours(0, 0, 0, 0);
          manualFacturasQuery = manualFacturasQuery.gte(
            "payment_due_date",
            fromDate.toISOString()
          );

          if (dateRange.to) {
            const toDate = new Date(dateRange.to);
            toDate.setHours(23, 59, 59, 999);
            manualFacturasQuery = manualFacturasQuery.lte(
              "payment_due_date",
              toDate.toISOString()
            );
          }
        }

        const { data: manualFacturasData, error: manualFacturasError } =
          await manualFacturasQuery;

        if (manualFacturasError) {
          console.error(
            "Error fetching late manual facturas:",
            manualFacturasError
          );
        }

        const allLatePayments: LatePayment[] = [];

        // Process sales_request data
        if (salesRequestData) {
          const processedSalesRequests = await Promise.all(
            salesRequestData.map(async (payment) => {
              const dueDate = new Date(payment.payment_due_date);
              dueDate.setHours(0, 0, 0, 0);
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              const timeDiff = today.getTime() - dueDate.getTime();
              const daysLate = Math.max(
                0,
                Math.floor(timeDiff / (1000 * 60 * 60 * 24))
              );

              const { data: paidAmountData, error: paidAmountError } =
                await supabase.rpc("get_sales_request_paid_amount", {
                  sales_request_id: payment.id,
                });

              if (paidAmountError) {
                console.error("Error fetching paid amount:", paidAmountError);
              }

              const paidAmount = paidAmountData || 0;

              const { data: paymentsData, error: paymentsError } =
                await supabase
                  .from("payments")
                  .select("*")
                  .eq("sales_request", payment.id);

              if (paymentsError) {
                console.error(
                  "Error fetching payments for sales request:",
                  paymentsError
                );
              }

              // Use total_amount and proportional_interest always as numbers
              const totalAmount = Number(payment.total_amount);
              const proportional_interest =
                payment.proportional_interest !== null &&
                payment.proportional_interest !== undefined
                  ? Number(payment.proportional_interest)
                  : daysLate > 0
                    ? (totalAmount - paidAmount > 0
                        ? totalAmount - paidAmount
                        : 0) *
                      (interestConfig / 100) *
                      (daysLate / 30)
                    : 0;

              // Always let paidAmount cover interest FIRST, then principal
              const interestLeft = proportional_interest;
              const principalLeft = totalAmount;
              const paid = paidAmount;

              const interestPaid = Math.min(paid, interestLeft);
              const remainingPaid = paid - interestPaid;
              const principalPaid = Math.min(remainingPaid, principalLeft);

              // The remaining owed is totalAmount + interest - paid (can't be less than 0)
              const pendingTotal = Math.max(
                0,
                totalAmount + proportional_interest - paidAmount
              );

              // Progress relative to total owed (principal + interest)
              let progressPercentage =
                (paidAmount / (totalAmount + proportional_interest)) * 100;
              progressPercentage = isNaN(progressPercentage)
                ? 0
                : Math.max(0, Math.min(100, progressPercentage));

              return {
                ...payment,
                daysLate,
                paid_amount: paidAmount,
                interest_left: Math.max(
                  0,
                  proportional_interest - interestPaid
                ),
                interest_paid: interestPaid,
                principal_paid: principalPaid,
                remaining_amount: Math.round(pendingTotal),
                payments: paymentsData || [],
                proportional_interest: proportional_interest,
                progress_percentage: progressPercentage,
                source: "sales_request" as const,
              };
            })
          );

          allLatePayments.push(...processedSalesRequests);
        }

        // Process manual_facturas data
        if (manualFacturasData) {
          const processedManualFacturas = manualFacturasData.map((factura) => {
            const dueDate = new Date(factura.payment_due_date);
            dueDate.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const timeDiff = today.getTime() - dueDate.getTime();
            const daysLate = Math.max(
              0,
              Math.floor(timeDiff / (1000 * 60 * 60 * 24))
            );

            const totalAmount = Number(factura.dues || factura.balance || 0);
            const paidAmount = Number(factura.paid || 0);

            // Calculate interest for manual facturas (similar to sales requests)
            const proportional_interest =
              daysLate > 0
                ? (totalAmount - paidAmount > 0
                    ? totalAmount - paidAmount
                    : 0) *
                  (interestConfig / 100) *
                  (daysLate / 30)
                : 0;

            // For manual facturas, we use the balance as the remaining amount
            const remainingAmount = Number(factura.balance || 0);

            // Progress based on how much has been paid vs what was originally owed
            let progressPercentage =
              totalAmount > 0
                ? ((totalAmount - remainingAmount) / totalAmount) * 100
                : 0;
            progressPercentage = Math.max(0, Math.min(100, progressPercentage));

            return {
              id: factura.id,
              total_amount: totalAmount,
              balance: factura.balance || 0,
              payment_due_date: factura.payment_due_date,
              client: {
                name:
                  (factura.clients as any)?.name || factura.client_name || "",
                tax_id:
                  (factura.clients as any)?.tax_id ||
                  factura.client_tax_id ||
                  "",
              },
              daysLate,
              paid_amount: paidAmount,
              remaining_amount: remainingAmount,
              payments: [],
              source: "manual_facturas" as const,
              doc_number: factura.doc_number,
              doc_type: factura.doc_type,
              proportional_interest: proportional_interest,
              progress_percentage: progressPercentage,
            };
          });

          allLatePayments.push(...processedManualFacturas);
        }

        return allLatePayments;
      } catch (error) {
        console.error("Error in late payments query:", error);
        return [];
      }
    },
  });

  // Sum (total_amount + interest - paid) for all late payments, never < 0
  const totalLatePayments = latePayments
    ? latePayments.reduce((sum, payment) => {
        if (payment.source === "sales_request") {
          const totalPlusInterest =
            Number(payment.total_amount) +
            (Number(payment.proportional_interest) || 0);
          const paid = Number(payment.paid_amount || 0);
          const pendingTotal = Math.max(0, totalPlusInterest - paid);
          return sum + Math.round(pendingTotal);
        } else {
          // For manual facturas, use the remaining amount (balance)
          return sum + Math.round(Number(payment.remaining_amount || 0));
        }
      }, 0)
    : 0;

  // Sum of all interests (not what's been paid yet, total that's been generated)
  const interestToCollect = latePayments
    ? latePayments.reduce(
        (sum, payment) =>
          sum + Math.round(Number(payment.proportional_interest) || 0),
        0
      )
    : 0;

  const getLatenessSeverity = (daysLate: number) => {
    if (daysLate < 30) return { class: "bg-yellow-500", label: "Leve" };
    if (daysLate < 60) return { class: "bg-orange-500", label: "Moderado" };
    return { class: "bg-red-500", label: "Crítico" };
  };

  const mapSeverity = (daysLate: number): "high" | "medium" | "low" => {
    if (daysLate >= 60) return "high";
    if (daysLate >= 30) return "medium";
    return "low";
  };

  return {
    latePayments: latePayments || [],
    isLoadingLatePayments: isLoadingLatePayments || isLoadingInterest,
    totalLatePayments,
    interestToCollect,
    getLatenessSeverity,
    mapSeverity,
  };
};
